import { create } from 'zustand'
import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc, writeBatch, serverTimestamp,
} from 'firebase/firestore'
import { db } from '../firebase'
import type { AACCard, AACConfig, AACLanguage, AACLabels } from '../types/aac'

const DEFAULT_CONFIG: AACConfig = {
  defaultLanguage: 'en',
  gridSize: 9,
  speakOnTap: false,
  appendPlease: false,
  quickPhrases: [],
  voiceURI: '',
  pitch: 1.0,
  rate: 1.0,
}

const CARDS_VERSION = 4

interface DefaultCardSeed { labels: AACLabels; emoji: string; category: string; order: number }

// Trilingual default board (en/tl/ceb) — TL/CEB preserved from the RN static
// set so Filipino families keep their language on a fresh board.
const DEFAULT_CARDS: DefaultCardSeed[] = [
  { labels: { en: 'Yes', tl: 'Oo', ceb: 'Oo' }, emoji: '✅', category: 'answers', order: 0 },
  { labels: { en: 'No', tl: 'Hindi', ceb: 'Dili' }, emoji: '❌', category: 'answers', order: 1 },
  { labels: { en: 'Stop', tl: 'Tigil', ceb: 'Hunong' }, emoji: '✋', category: 'actions', order: 2 },
  { labels: { en: 'Help', tl: 'Tulong', ceb: 'Tabang' }, emoji: '🙋', category: 'needs', order: 3 },
  { labels: { en: "I'm okay", tl: 'Ayos lang', ceb: 'Okay na' }, emoji: '👍', category: 'needs', order: 4 },
  { labels: { en: 'Hungry', tl: 'Gutom', ceb: 'Gutom' }, emoji: '🍽️', category: 'needs', order: 5 },
  { labels: { en: 'Thirsty', tl: 'Uhaw', ceb: 'Uhaw' }, emoji: '💧', category: 'needs', order: 6 },
  { labels: { en: 'Tired', tl: 'Pagod', ceb: 'Gipoy' }, emoji: '😴', category: 'needs', order: 7 },
  { labels: { en: 'Toilet', tl: 'Banyo', ceb: 'CR' }, emoji: '🚽', category: 'needs', order: 8 },
  { labels: { en: 'Pain', tl: 'Masakit', ceb: 'Sakit' }, emoji: '🤕', category: 'feelings', order: 9 },
  { labels: { en: 'Head hurts', tl: 'Masakit ulo', ceb: 'Sakit ulo' }, emoji: '🤯', category: 'feelings', order: 10 },
  { labels: { en: 'Tooth hurts', tl: 'Masakit ngipin', ceb: 'Sakit ngipon' }, emoji: '🦷', category: 'feelings', order: 11 },
  { labels: { en: 'Too loud', tl: 'Maingay', ceb: 'Kusog kaayo' }, emoji: '🙉', category: 'overwhelmed', order: 12 },
  { labels: { en: 'Too much', tl: 'Sobra', ceb: 'Sobra' }, emoji: '😵', category: 'overwhelmed', order: 13 },
  { labels: { en: 'Need quiet', tl: 'Tahimik', ceb: 'Hilom' }, emoji: '🤫', category: 'overwhelmed', order: 14 },
  { labels: { en: 'Scared', tl: 'Takot', ceb: 'Hadlok' }, emoji: '😨', category: 'feelings', order: 15 },
  { labels: { en: 'Crying', tl: 'Umiiyak', ceb: 'Nagahilak' }, emoji: '😭', category: 'feelings', order: 16 },
  { labels: { en: 'Happy', tl: 'Masaya', ceb: 'Malipayon' }, emoji: '😊', category: 'feelings', order: 17 },
  { labels: { en: 'Sad', tl: 'Malungkot', ceb: 'Nalungon' }, emoji: '😢', category: 'feelings', order: 18 },
  { labels: { en: 'Angry', tl: 'Galit', ceb: 'Suko' }, emoji: '😠', category: 'feelings', order: 19 },
  { labels: { en: 'Calm', tl: 'Panatag', ceb: 'Malinaw' }, emoji: '😌', category: 'feelings', order: 20 },
  { labels: { en: 'More', tl: 'Pa', ceb: 'Dugang' }, emoji: '➕', category: 'answers', order: 21 },
  { labels: { en: 'Go', tl: 'Lakad', ceb: 'Lakaw' }, emoji: '🚶', category: 'actions', order: 22 },
  { labels: { en: 'Home', tl: 'Bahay', ceb: 'Balay' }, emoji: '🏠', category: 'places', order: 23 },
  { labels: { en: 'I love you', tl: 'Mahal kita', ceb: 'Gihigugma ko' }, emoji: '💕', category: 'feelings', order: 24 },
  { labels: { en: 'Hug', tl: 'Yakap', ceb: 'Yapos' }, emoji: '🤗', category: 'feelings', order: 25 },
  { labels: { en: 'Thank you', tl: 'Salamat', ceb: 'Salamat' }, emoji: '🙏', category: 'answers', order: 26 },
  { labels: { en: 'Miss you', tl: 'Miss kita', ceb: 'Gikulangan' }, emoji: '🥹', category: 'feelings', order: 27 },
  { labels: { en: 'Good job', tl: 'Magaling', ceb: 'Maayo' }, emoji: '⭐', category: 'answers', order: 28 },
  { labels: { en: 'Play', tl: 'Laro', ceb: 'Dula' }, emoji: '🎮', category: 'actions', order: 29 },
  { labels: { en: 'Music', tl: 'Musika', ceb: 'Musika' }, emoji: '🎵', category: 'actions', order: 30 },
  { labels: { en: 'Outside', tl: 'Labas', ceb: 'Gawas' }, emoji: '🌳', category: 'places', order: 31 },
]

let _ownerUid: string | null = null
let _profileId: string | null = null

function cardsPath(ownerUid: string, profileId: string) {
  return collection(db, 'users', ownerUid, 'profiles', profileId, 'aac_cards')
}

function configDocRef(ownerUid: string, profileId: string) {
  return doc(db, 'users', ownerUid, 'profiles', profileId, 'aac', 'config')
}

export interface AACState {
  config: AACConfig
  cards: AACCard[]
  archivedCards: AACCard[]
  loading: boolean
  visibleCards: () => AACCard[]
  setCards: (cards: AACCard[]) => void
  loadForProfile: (ownerUid: string, profileId: string) => Promise<void>
  addCard: (ownerUid: string, profileId: string, label: string, emoji: string, category?: string, labels?: AACLabels) => Promise<void>
  addCards: (ownerUid: string, profileId: string, cards: Array<{ label: string; emoji: string; category: string; labels?: AACLabels }>) => Promise<void>
  updateCard: (ownerUid: string, profileId: string, cardId: string, updates: Partial<Pick<AACCard, 'label' | 'emoji' | 'imageUrl' | 'hidden' | 'category'>>) => Promise<void>
  toggleCardVisibility: (ownerUid: string, profileId: string, cardId: string) => Promise<void>
  archiveCard: (ownerUid: string, profileId: string, cardId: string) => Promise<void>
  restoreCard: (ownerUid: string, profileId: string, cardId: string) => Promise<void>
  deleteCardPermanently: (ownerUid: string, profileId: string, cardId: string) => Promise<void>
  saveCardOrder: (ownerUid: string, profileId: string) => Promise<void>
  addQuickPhrase: (phrase: string) => Promise<void>
  removeQuickPhrase: (phrase: string) => Promise<void>
  patchConfig: (updates: Partial<AACConfig>, persist?: boolean) => void
  saveSettings: () => Promise<void>
  setLanguage: (lang: AACLanguage) => void
  reset: () => void
}

export const useAACStore = create<AACState>()((set, get) => ({
  config: { ...DEFAULT_CONFIG, quickPhrases: [] },
  cards: [],
  archivedCards: [],
  loading: false,

  visibleCards: () => get().cards.filter(c => !c.hidden),

  setCards: (cards) => set({ cards }),

  async loadForProfile(ownerUid, profileId) {
    if (_profileId === profileId && get().cards.length > 0) return
    _ownerUid = ownerUid
    _profileId = profileId
    set({ loading: true })
    try {
      const snap = await getDoc(configDocRef(ownerUid, profileId))
      if (snap.exists()) {
        const data = snap.data() as Record<string, unknown>
        set({
          config: {
            defaultLanguage: (data.defaultLanguage as AACLanguage) ?? DEFAULT_CONFIG.defaultLanguage,
            gridSize: (data.gridSize as 4 | 6 | 9) ?? DEFAULT_CONFIG.gridSize,
            speakOnTap: (data.speakOnTap as boolean) ?? DEFAULT_CONFIG.speakOnTap,
            appendPlease: (data.appendPlease as boolean) ?? DEFAULT_CONFIG.appendPlease,
            quickPhrases: (data.quickPhrases as string[]) ?? [],
            voiceURI: (data.voiceURI as string) ?? '',
            pitch: (data.pitch as number) ?? DEFAULT_CONFIG.pitch,
            rate: (data.rate as number) ?? DEFAULT_CONFIG.rate,
          },
        })
      }
      const cardsSnap = await getDocs(cardsPath(ownerUid, profileId))
      if (cardsSnap.empty) {
        await seedDefaultCards(ownerUid, profileId)
        return
      }
      const all = cardsSnap.docs.map(d => {
        const data = d.data() as Record<string, unknown>
        return {
          id: d.id,
          label: (data.label as string) ?? '',
          emoji: (data.emoji as string) ?? '',
          imageUrl: (data.imageUrl as string) ?? '',
          category: (data.category as string) ?? '',
          order: (data.order as number) ?? 0,
          archived: (data.archived as boolean) ?? false,
          hidden: (data.hidden as boolean) ?? false,
        } satisfies AACCard
      })
      const active = all.filter(c => !c.archived).sort((a, b) => a.order - b.order)
      set({ cards: active, archivedCards: all.filter(c => c.archived) })
      const existingLabels = new Set(all.map(c => c.label.toLowerCase()))
      const maxOrder = all.reduce((m, c) => Math.max(m, c.order), -1)
      await appendMissingDefaults(ownerUid, profileId, existingLabels, maxOrder)
    } catch (err) {
      console.warn('[aac] loadForProfile error:', err)
    } finally {
      set({ loading: false })
    }
  },

  async addCard(ownerUid, profileId, label, emoji, category = 'custom', labels) {
    const trimmed = label.trim()
    // labels.en is authoritative when provided; mirror into legacy `label`
    const en = (labels?.en ?? trimmed).trim()
    if (!en) return
    const resolvedLabels: AACLabels = { en, ...(labels?.tl ? { tl: labels.tl.trim() } : {}), ...(labels?.ceb ? { ceb: labels.ceb.trim() } : {}) }
    const newOrder = get().cards.length
    const cardRef = doc(cardsPath(ownerUid, profileId))
    const card: AACCard = { id: cardRef.id, label: en, labels: resolvedLabels, emoji: emoji.trim() || '💬', imageUrl: '', category, order: newOrder }
    try {
      await setDoc(cardRef, { ...card, _v: CARDS_VERSION, createdAt: serverTimestamp() })
      set({ cards: [...get().cards, card] })
    } catch (err) {
      console.warn('[aac] addCard error:', err)
    }
  },

  async addCards(ownerUid, profileId, incoming) {
    // Bulk-create (templates). Idempotent: skip cards whose (en label + category)
    // already exist, so re-applying a template pack never duplicates tiles.
    const existing = new Set(get().cards.map(c => `${(c.labels?.en ?? c.label).toLowerCase()}|${c.category}`))
    const fresh = incoming.filter(c => {
      const en = (c.labels?.en ?? c.label).trim()
      return en && !existing.has(`${en.toLowerCase()}|${c.category}`)
    })
    if (fresh.length === 0) return
    let nextOrder = get().cards.length
    const batch = writeBatch(db)
    const created: AACCard[] = []
    for (const c of fresh) {
      const en = (c.labels?.en ?? c.label).trim()
      const resolvedLabels: AACLabels = { en, ...(c.labels?.tl ? { tl: c.labels.tl.trim() } : {}), ...(c.labels?.ceb ? { ceb: c.labels.ceb.trim() } : {}) }
      const cardRef = doc(cardsPath(ownerUid, profileId))
      const card: AACCard = { id: cardRef.id, label: en, labels: resolvedLabels, emoji: (c.emoji || '💬').trim(), imageUrl: '', category: c.category, order: nextOrder++ }
      batch.set(cardRef, { ...card, _v: CARDS_VERSION, createdAt: serverTimestamp() })
      created.push(card)
    }
    try {
      await batch.commit()
      set({ cards: [...get().cards, ...created] })
    } catch (err) {
      console.warn('[aac] addCards error:', err)
    }
  },

  async updateCard(ownerUid, profileId, cardId, updates) {
    try {
      await updateDoc(doc(cardsPath(ownerUid, profileId), cardId), updates as Record<string, unknown>)
      const idx = get().cards.findIndex(c => c.id === cardId)
      if (idx >= 0) {
        const updated = [...get().cards]
        updated[idx] = { ...updated[idx], ...updates }
        set({ cards: updated })
      }
    } catch (err) {
      console.warn('[aac] updateCard error:', err)
    }
  },

  async toggleCardVisibility(ownerUid, profileId, cardId) {
    const card = get().cards.find(c => c.id === cardId)
    if (!card) return
    await get().updateCard(ownerUid, profileId, cardId, { hidden: !card.hidden })
  },

  async archiveCard(ownerUid, profileId, cardId) {
    try {
      await updateDoc(doc(cardsPath(ownerUid, profileId), cardId), { archived: true })
      const card = get().cards.find(c => c.id === cardId)
      if (card) {
        set({
          archivedCards: [{ ...card, archived: true }, ...get().archivedCards],
          cards: get().cards.filter(c => c.id !== cardId),
        })
      }
    } catch (err) {
      console.warn('[aac] archiveCard error:', err)
    }
  },

  async restoreCard(ownerUid, profileId, cardId) {
    try {
      const newOrder = get().cards.length
      await updateDoc(doc(cardsPath(ownerUid, profileId), cardId), { archived: false, order: newOrder })
      const card = get().archivedCards.find(c => c.id === cardId)
      if (card) {
        set({
          cards: [...get().cards, { ...card, archived: false, order: newOrder }],
          archivedCards: get().archivedCards.filter(c => c.id !== cardId),
        })
      }
    } catch (err) {
      console.warn('[aac] restoreCard error:', err)
    }
  },

  async deleteCardPermanently(ownerUid, profileId, cardId) {
    try {
      await deleteDoc(doc(cardsPath(ownerUid, profileId), cardId))
      set({ archivedCards: get().archivedCards.filter(c => c.id !== cardId) })
    } catch (err) {
      console.warn('[aac] deleteCardPermanently error:', err)
    }
  },

  async saveCardOrder(ownerUid, profileId) {
    try {
      const batch = writeBatch(db)
      get().cards.forEach((card, i) => {
        batch.update(doc(cardsPath(ownerUid, profileId), card.id), { order: i })
      })
      await batch.commit()
      set({ cards: get().cards.map((c, i) => ({ ...c, order: i })) })
    } catch (err) {
      console.warn('[aac] saveCardOrder error:', err)
    }
  },

  async addQuickPhrase(phrase) {
    const trimmed = phrase.trim()
    if (!trimmed || get().config.quickPhrases.includes(trimmed)) return
    const updated = { ...get().config, quickPhrases: [...get().config.quickPhrases, trimmed] }
    set({ config: updated })
    if (_ownerUid && _profileId) {
      try {
        await setDoc(configDocRef(_ownerUid, _profileId), updated, { merge: true })
      } catch (err) {
        console.warn('[aac] addQuickPhrase error:', err)
      }
    }
  },

  async removeQuickPhrase(phrase) {
    const updated = { ...get().config, quickPhrases: get().config.quickPhrases.filter(p => p !== phrase) }
    set({ config: updated })
    if (_ownerUid && _profileId) {
      try {
        await setDoc(configDocRef(_ownerUid, _profileId), updated, { merge: true })
      } catch (err) {
        console.warn('[aac] removeQuickPhrase error:', err)
      }
    }
  },

  patchConfig(updates, persist = true) {
    set({ config: { ...get().config, ...updates } })
    if (persist) void get().saveSettings()
  },

  async saveSettings() {
    if (_ownerUid && _profileId) {
      try {
        await setDoc(configDocRef(_ownerUid, _profileId), { ...get().config }, { merge: true })
      } catch (err) {
        console.warn('[aac] saveSettings error:', err)
      }
    }
  },

  setLanguage(lang) {
    set({ config: { ...get().config, defaultLanguage: lang } })
  },

  reset() {
    _ownerUid = null
    _profileId = null
    set({ config: { ...DEFAULT_CONFIG, quickPhrases: [] }, cards: [], archivedCards: [] })
  },
}))

async function seedDefaultCards(ownerUid: string, profileId: string) {
  const seeded: AACCard[] = []
  for (const seed of DEFAULT_CARDS) {
    const cardRef = doc(collection(db, 'users', ownerUid, 'profiles', profileId, 'aac_cards'))
    const card: AACCard = { id: cardRef.id, label: seed.labels.en, labels: seed.labels, emoji: seed.emoji, imageUrl: '', category: seed.category, order: seed.order }
    try {
      await setDoc(cardRef, { ...card, _v: CARDS_VERSION, createdAt: serverTimestamp() })
      seeded.push(card)
    } catch (err) {
      console.warn('[aac] seedDefaultCards write error:', err)
    }
  }
  useAACStore.setState({ cards: seeded, loading: false })
}

async function appendMissingDefaults(ownerUid: string, profileId: string, existingLabels: Set<string>, maxOrder: number) {
  let nextOrder = maxOrder + 1
  for (const seed of DEFAULT_CARDS) {
    if (existingLabels.has(seed.labels.en.toLowerCase())) continue
    const cardRef = doc(collection(db, 'users', ownerUid, 'profiles', profileId, 'aac_cards'))
    const card: AACCard = { id: cardRef.id, label: seed.labels.en, labels: seed.labels, emoji: seed.emoji, imageUrl: '', category: seed.category, order: nextOrder++ }
    try {
      await setDoc(cardRef, { ...card, _v: CARDS_VERSION, createdAt: serverTimestamp() })
      useAACStore.setState(s => ({ cards: [...s.cards, card] }))
    } catch (err) {
      console.warn('[aac] appendMissingDefaults error:', err)
    }
  }
}
